import { Box } from '@mui/system';
import React, { useCallback } from 'react';
import useAtlasV2KeywordSearchRequestBuilder from 'src/components/AtlasV2Components/AtlasV2Sidebar/AtlasV2SearchSidebar/useAtlasV2KeywordSearchRequestBuilder';
import { AtlasV2TopicBuilderCreateResults } from 'src/components/AtlasV2Components/AtlasV2TopicPageComponents/AtlasV2TopicBuilderCreateResults';
import AtlasV2ProductListContainer from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2ProductListContainer';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { SegmentDeletionModal } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentDeletionModal';
import SegmentNoDataPlaceholder from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentNoDataPlaceholder';
import { BeaconProSidebarFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import { LegacyTopicBuilderSidebar } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/LegacyTopicBuilderSidebar';
import {
  FILTER_OPERATION,
  useSavedSearchBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/hooks/useSavedSearchBuilder';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { BEACON_CHART_SPACING, BEACON_PRO_SIDEBAR_WIDTH } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { getQueryParamValue } from 'src/utils/app';
import { GenericNavigationTabs } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import { RouteComponentProps } from 'react-router-dom';
import { AtlasV2SegmentBuilderSidebar } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/SegmentsV2/AtlasV2SegmentBuilderSidebar';

interface EntityTypeTabsProps {
  selectedTab: 'segment' | 'searchtermlist';
  setTab: (selectedTab: 'segment' | 'searchtermlist') => void;
  availableTabs: (
    | {
        label: 'Product';
        value: 'segment';
      }
    | {
        label: 'Keyword';
        value: 'searchtermlist';
      }
  )[];
}
/**
 * List of tabs for the top of the entity builder page
 */
const EntityTypeTabs = ({ selectedTab, availableTabs, setTab }: EntityTypeTabsProps) => {
  return (
    <GenericNavigationTabs
      tab={selectedTab}
      tabs={availableTabs.map(({ label, value }) => {
        return {
          label,
          onClick: () => setTab(value),
          value
        };
      })}
    />
  );
};

/**
 * Renders a Product Grid with Segment Builder results.
 */
const SegmentBuilderResults = () => {
  const keywordSearchRequestBuilder = useAtlasV2KeywordSearchRequestBuilder();
  const { updateSavedSearch } = useSavedSearchBuilder();

  // Handles quick exclude button click
  const handleExcludeButtonClick = (term: string) => {
    updateSavedSearch({
      operation: FILTER_OPERATION.Add,
      key: BeaconProSidebarFilters.ExcludedKeyword.filterKey,
      newValue: term
    });
  };

  return (
    <AtlasV2ProductListContainer
      productGridProps={{
        showExcludeProductButton: true,
        handleExcludeProduct: handleExcludeButtonClick,
        noDataPlaceholder: (
          <Flex justifyContent="center">
            <SegmentNoDataPlaceholder
              title="No results found"
              subtitle="Please refine your filters and try again."
              maxWidth={150}
            />
          </Flex>
        )
      }}
      entityTableHeaderProps={{}}
      fields={[
        {
          name: 'retailSales'
        },
        {
          name: 'unitsSold'
        },
        {
          name: 'retailPrice'
        }
      ]}
      showHeader={false}
      showGrid
      forceGrid
      indexName="sales"
      sortFieldName="retailSales"
      useTableDataProps={{
        buildRequest: keywordSearchRequestBuilder((additionalBuilder) => {
          return additionalBuilder;
        })
      }}
    />
  );
};

export const AtlasV2SegmentTopicBuilderPage = ({ history }: RouteComponentProps) => {
  const entityType = getQueryParamValue('entityType');
  const entityIdBeingEdited = getQueryParamValue('id');
  const isTopicEntityType = entityType === 'searchtermlist';
  const isSearchResultsPage = !!getQueryParamValue('searchPage');

  const pageTitle = isSearchResultsPage ? 'Search Results' : isTopicEntityType ? 'Topic Builder' : 'Segment Builder';
  const availableTabs: EntityTypeTabsProps['availableTabs'] = isSearchResultsPage
    ? [
        {
          label: 'Product',
          value: 'segment'
        },
        {
          label: 'Keyword',
          value: 'searchtermlist'
        }
      ]
    : isTopicEntityType
    ? [{ label: 'Keyword', value: 'searchtermlist' }]
    : [{ label: 'Product', value: 'segment' }];

  const [deleteEntityModalOpen, setDeleteEntityModalOpen] = React.useState(false);
  const [entityToDelete, setEntityToDelete] = React.useState(null);

  const renderSidebar = useCallback(() => {
    switch (entityType) {
      case 'searchtermlist':
        return <LegacyTopicBuilderSidebar />;
      case 'segment':
        return <AtlasV2SegmentBuilderSidebar />;
      default:
        return null;
    }
  }, [entityType]);

  const renderBuilderResults = useCallback(() => {
    switch (entityType) {
      case 'searchtermlist':
        return <AtlasV2TopicBuilderCreateResults />;
      case 'segment':
        return <SegmentBuilderResults />;
      default:
        return null;
    }
  }, [entityType]);

  return (
    <BasePageContainer sx={{ marginTop: '0px', display: 'flex', gap: '64px', height: 'auto' }}>
      <Box sx={{ width: BEACON_PRO_SIDEBAR_WIDTH }}>{renderSidebar()}</Box>
      <Flex flexDirection="column" sx={{ width: '100%' }}>
        <Flex sx={{ height: '40px' }} justifyContent="space-between">
          <Text variant="h2">{pageTitle}</Text>
          {entityIdBeingEdited && (
            <SlButton
              onClick={() => {
                setDeleteEntityModalOpen(true);
                setEntityToDelete(entityIdBeingEdited);
              }}
            >
              Delete
            </SlButton>
          )}
        </Flex>
        <EntityTypeTabs
          selectedTab={isTopicEntityType ? 'searchtermlist' : 'segment'}
          availableTabs={availableTabs}
          setTab={(newTab) => {
            const params = new URLSearchParams(window.location.search);
            params.set('entityType', newTab);
            history.push({ search: params.toString(), state: { searchPage: isSearchResultsPage } });
          }}
        />
        <SegmentDeletionModal
          isTopicSegment={false}
          segmentToDelete={entityToDelete}
          open={deleteEntityModalOpen}
          handleClose={() => {
            setDeleteEntityModalOpen(false);
            setEntityToDelete(null);
          }}
        />

        <Box marginBottom="32px"></Box>
        <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
          {renderBuilderResults()}
        </Flex>
      </Flex>
    </BasePageContainer>
  );
};
